// ScenarioImportService — parses and validates user-uploaded scenario files.
//
// Accepts a FileList (from <input type="file" multiple> or drag-and-drop),
// identifies manifest.json, matches scenario + topology files against it,
// and returns validated data ready for dispatch to scenarioSourceSlice.
//
// All parsing is purely in-browser via FileReader — no network requests.

import type { FlowManifest, SimulationScenario, SimulationTopology } from '../types/index.ts';
import type { EventCatalog, ImpactAnalysis } from '../types/moment-artifacts.ts';
import type { SagaDefinition } from '../store/sagas/sagaBehavioralEvaluator.ts';
import {
  parsePolicyDefinitions,
  type PolicyDefinition,
} from '../store/sagas/policyChainEvaluator.ts';
import {
  validateTopology as validateTopologyContract,
  validateSeed,
  validateSeedStructure,
  formatValidationErrors,
} from '../contracts/moment-ir-validation.ts';
import { loadLocalArtifacts } from './ArtifactLoaderService.ts';

// ---------------------------------------------------------------------------
// Result types
// ---------------------------------------------------------------------------

export interface ImportedArtifacts {
  eventCatalog: EventCatalog | null;
  impactAnalysis: ImpactAnalysis | null;
  sagaDefinitions: SagaDefinition[] | null;
  /** Null = artifact absent/malformed (policy tier will surface a skip). */
  policyDefinitions: PolicyDefinition[] | null;
}

export interface ImportResult {
  manifest: FlowManifest;
  topologies: Record<string, SimulationTopology>;
  scenarios: Record<string, SimulationScenario>;
  warnings: ImportWarning[];
  artifacts: ImportedArtifacts;
}

export interface ImportWarning {
  file: string;
  message: string;
}

// ---------------------------------------------------------------------------
// File reading helper
// ---------------------------------------------------------------------------

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
    reader.readAsText(file);
  });
}

function parseJSON<T>(text: string, fileName: string): T {
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error(`Invalid JSON in ${fileName}`);
  }
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

function validateManifest(data: unknown, fileName: string): FlowManifest {
  if (!data || typeof data !== 'object') {
    throw new Error(`${fileName}: manifest must be a JSON object`);
  }
  const obj = data as Record<string, unknown>;
  if (!Array.isArray(obj.flows)) {
    throw new Error(`${fileName}: manifest must have a "flows" array`);
  }
  for (const flow of obj.flows as unknown[]) {
    if (!flow || typeof flow !== 'object') {
      throw new Error(`${fileName}: each flow must be an object`);
    }
    const f = flow as Record<string, unknown>;
    if (typeof f.flowId !== 'string' || typeof f.flowName !== 'string') {
      throw new Error(`${fileName}: each flow must have flowId and flowName`);
    }
    if (typeof f.topology !== 'string') {
      throw new Error(`${fileName}: each flow must have a topology filename`);
    }
    if (!Array.isArray(f.scenarios)) {
      throw new Error(`${fileName}: each flow must have a scenarios array`);
    }
    // Validate each scenario entry in the manifest
    for (const scenario of f.scenarios as unknown[]) {
      if (!scenario || typeof scenario !== 'object') {
        throw new Error(`${fileName}: each scenario entry must be an object`);
      }
      const s = scenario as Record<string, unknown>;
      if (typeof s.scenarioId !== 'string') {
        throw new Error(`${fileName}: each scenario entry must have scenarioId`);
      }
      if (typeof s.scenarioLabel !== 'string') {
        throw new Error(`${fileName}: each scenario entry must have scenarioLabel`);
      }
      if (typeof s.file !== 'string') {
        throw new Error(`${fileName}: each scenario entry must have file`);
      }
    }
  }
  return data as FlowManifest;
}

function validateScenario(data: unknown, fileName: string): SimulationScenario {
  if (!data || typeof data !== 'object') {
    throw new Error(`${fileName}: scenario must be a JSON object`);
  }
  const obj = data as Record<string, unknown>;
  if (typeof obj.scenarioId !== 'string') {
    throw new Error(`${fileName}: scenario must have scenarioId`);
  }
  if (typeof obj.scenarioLabel !== 'string') {
    throw new Error(`${fileName}: scenario must have scenarioLabel`);
  }
  if (typeof obj.description !== 'string') {
    throw new Error(`${fileName}: scenario must have description`);
  }
  if (!Array.isArray(obj.expectedPath)) {
    throw new Error(`${fileName}: scenario must have expectedPath array`);
  }
  if (!Array.isArray(obj.activeBranches)) {
    throw new Error(`${fileName}: scenario must have activeBranches array`);
  }
  if (!Array.isArray(obj.events)) {
    throw new Error(`${fileName}: scenario must have events array`);
  }
  if (!(obj.events as unknown[]).every((e) => e !== null && typeof e === 'object')) {
    throw new Error(`${fileName}: scenario events must contain only objects`);
  }
  return data as SimulationScenario;
}

function validateTopology(data: unknown, fileName: string): SimulationTopology {
  if (!data || typeof data !== 'object') {
    throw new Error(`${fileName}: topology must be a JSON object`);
  }
  const obj = data as Record<string, unknown>;
  if (!Array.isArray(obj.lanes)) {
    throw new Error(`${fileName}: topology must have lanes array`);
  }
  if (!Array.isArray(obj.frames)) {
    throw new Error(`${fileName}: topology must have frames array`);
  }
  // Normalize optional fields to safe defaults
  return {
    ...(obj as Record<string, unknown>),
    lanes: obj.lanes,
    frames: obj.frames,
    connections: Array.isArray(obj.connections) ? obj.connections : [],
    eventRouting:
      obj.eventRouting && typeof obj.eventRouting === 'object' && !Array.isArray(obj.eventRouting)
        ? obj.eventRouting
        : {},
    branchPredicates:
      obj.branchPredicates &&
      typeof obj.branchPredicates === 'object' &&
      !Array.isArray(obj.branchPredicates)
        ? obj.branchPredicates
        : {},
  } as SimulationTopology;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Import a set of files uploaded by the user. Expects:
 * - One manifest.json
 * - Topology files referenced by the manifest (one per flow)
 * - Scenario files referenced by the manifest
 *
 * Returns validated data ready for dispatch, plus warnings for any missing files.
 */
export async function importFileSet(
  files: FileList | File[],
  extraWarnings?: ImportWarning[],
): Promise<ImportResult> {
  const fileArray = Array.from(files);

  if (fileArray.length === 0) {
    throw new Error('No files selected');
  }

  // Build a lookup by filename
  const fileMap = new Map<string, File>();
  for (const file of fileArray) {
    fileMap.set(file.name, file);
  }

  // 1. Find and parse manifest.json
  const manifestFile = fileMap.get('manifest.json');
  if (!manifestFile) {
    throw new Error('No manifest.json found in uploaded files');
  }

  const manifestText = await readFileAsText(manifestFile);
  const manifestData = parseJSON<unknown>(manifestText, 'manifest.json');
  const manifest = validateManifest(manifestData, 'manifest.json');

  // 2. Parse topologies and scenarios
  const topologies: Record<string, SimulationTopology> = {};
  const scenarios: Record<string, SimulationScenario> = {};
  const warnings: ImportWarning[] = extraWarnings ? [...extraWarnings] : [];

  for (const flow of manifest.flows) {
    // Topology — only process if file was uploaded
    const topoFile = fileMap.get(flow.topology);
    if (topoFile) {
      const topoText = await readFileAsText(topoFile);
      const topoData = parseJSON<unknown>(topoText, flow.topology);
      const topology = validateTopology(topoData, flow.topology);

      // Contract-level validation (MomentData IR namespaces). An invalid
      // topology is excluded with a visible warning — never loaded silently.
      const topoValidation = validateTopologyContract(topology);
      if (!topoValidation.valid) {
        warnings.push({
          file: flow.topology,
          message: `Topology failed validation and was not loaded: ${formatValidationErrors(topoValidation)}`,
        });
      } else {
        topologies[flow.flowId] = topology;
      }
    }

    // Scenarios — only process files that were uploaded
    for (const entry of flow.scenarios) {
      const scenarioFile = fileMap.get(entry.file);
      if (scenarioFile) {
        const scenarioText = await readFileAsText(scenarioFile);
        const scenarioData = parseJSON<unknown>(scenarioText, entry.file);
        const scenario = validateScenario(scenarioData, entry.file);

        if (scenario.scenarioId !== entry.scenarioId) {
          throw new Error(
            `Scenario file "${entry.file}" contains scenarioId "${scenario.scenarioId}" but manifest expects "${entry.scenarioId}"`,
          );
        }

        // Contract-level validation: full seed-vs-topology when this flow's
        // topology was uploaded and valid, envelope-only otherwise. Invalid
        // scenarios are excluded with a visible warning.
        const flowTopology = topologies[flow.flowId];
        const seedValidation = flowTopology
          ? validateSeed(scenario, flowTopology)
          : validateSeedStructure(scenario);
        if (!seedValidation.valid) {
          warnings.push({
            file: entry.file,
            message: `Scenario failed validation and was not loaded: ${formatValidationErrors(seedValidation)}`,
          });
          continue;
        }

        scenarios[entry.scenarioId] = scenario;
      }
    }
  }

  // 3. Supplementary artifacts (event catalog, saga state machines, impact
  // analysis). Loaded from the upload when present; the artifact cache is
  // always re-seeded from THIS import so a previous source's artifacts
  // (e.g. the remote TicketWave example) cannot leak into this project.
  const artifacts = await importArtifacts(fileMap, manifest, warnings);
  loadLocalArtifacts(
    {
      eventCatalog: artifacts.eventCatalog ?? undefined,
      impactAnalysis: artifacts.impactAnalysis ?? undefined,
      sagaDefinitions: artifacts.sagaDefinitions ?? undefined,
      policyDefinitions: artifacts.policyDefinitions ?? undefined,
    },
    'user-import',
  );

  return { manifest, topologies, scenarios, warnings, artifacts };
}

// ---------------------------------------------------------------------------
// Artifact imports (optional files)
// ---------------------------------------------------------------------------

async function parseOptionalArtifact<T>(
  fileMap: Map<string, File>,
  fileNames: (string | undefined)[],
  warnings: ImportWarning[],
): Promise<T | null> {
  for (const name of fileNames) {
    if (!name) continue;
    const file = fileMap.get(name);
    if (!file) continue;
    try {
      const text = await readFileAsText(file);
      return parseJSON<T>(text, name);
    } catch (err) {
      warnings.push({
        file: name,
        message: err instanceof Error ? err.message : 'Failed to parse artifact file',
      });
      return null;
    }
  }
  return null;
}

async function importArtifacts(
  fileMap: Map<string, File>,
  manifest: FlowManifest,
  warnings: ImportWarning[],
): Promise<ImportedArtifacts> {
  const refs = manifest.artifacts;
  const eventCatalog = await parseOptionalArtifact<EventCatalog>(
    fileMap,
    [refs?.eventCatalog, 'event-catalog.json'],
    warnings,
  );
  const impactAnalysis = await parseOptionalArtifact<ImpactAnalysis>(
    fileMap,
    [refs?.impactAnalysis, 'impact-analysis.json'],
    warnings,
  );
  const sagaDefinitions = await parseOptionalArtifact<SagaDefinition[]>(
    fileMap,
    [refs?.sagaStateMachines, 'saga-state-machines.json'],
    warnings,
  );

  // policy-definitions.json — accepted by manifest reference or default name.
  // Present-but-malformed content must NOT read as "no policies declared":
  // it is normalized to null (artifact absent) with a visible warning, so the
  // policy tier surfaces an explicit skip instead of silence.
  const rawPolicyDefinitions = await parseOptionalArtifact<unknown>(
    fileMap,
    [refs?.policyDefinitions, 'policy-definitions.json'],
    warnings,
  );
  let policyDefinitions: PolicyDefinition[] | null = null;
  if (rawPolicyDefinitions !== null) {
    policyDefinitions = parsePolicyDefinitions(rawPolicyDefinitions);
    if (policyDefinitions === null) {
      warnings.push({
        file: refs?.policyDefinitions ?? 'policy-definitions.json',
        message:
          'Policy definitions file does not match the expected contract — policy chain assertions will be skipped',
      });
    }
  }

  return { eventCatalog, impactAnalysis, sagaDefinitions, policyDefinitions };
}

/**
 * Get the total expected file count from a manifest (manifest + topologies + scenarios).
 */
export function expectedFileCount(manifest: FlowManifest): number {
  let count = 1; // manifest.json itself
  for (const flow of manifest.flows) {
    count += 1; // topology
    count += flow.scenarios.length;
  }
  return count;
}
